import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { toast } from "sonner";
import Timeline from "./Timeline";
import CityTaxCollectPanel from "./CityTaxCollectPanel";
import CustomerJourney from "./CustomerJourney";
import CheckinWorkflowPanel from "./CheckinWorkflowPanel";
import GuestPortalLink from "./GuestPortalLink";
import { useAuth } from "../AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { eur } from "../helpers";
import {
  Pencil, Mail, MessageCircle, Send, FileText, BookOpen, KeyRound, BarChart3, CalendarOff,
  Wrench, Sparkles, Phone, User, CreditCard, FileCheck, Clock, Building2,
} from "lucide-react";

const onlyDigits = (s) => (s || "").replace(/[^\d]/g, "");

export default function BookingDetailDialog({ open, onOpenChange, bookingId, canEdit, onEdit, onBlockDates }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [tl, setTl] = useState(null);
  const [tlLoading, setTlLoading] = useState(false);

  const loadTimeline = useCallback(() => {
    if (!bookingId) return;
    setTlLoading(true);
    api.get(`/bookings/${bookingId}/timeline`).then((r) => setTl(r.data)).catch(() => setTl(null)).finally(() => setTlLoading(false));
  }, [bookingId]);

  const load = useCallback(() => {
    if (!bookingId) return;
    api.get(`/bookings/${bookingId}/detail`).then((r) => setD(r.data)).catch(() => setD(null));
    loadTimeline();
  }, [bookingId, loadTimeline]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const addNote = async (payload) => {
    try {
      await api.post(`/bookings/${bookingId}/timeline/notes`, payload);
      toast.success("Nota aggiunta alla cronologia");
      loadTimeline();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const voidNote = async (noteId) => {
    if (!window.confirm("Annullare questa nota? Lo storico verrà mantenuto.")) return;
    try {
      await api.post(`/bookings/timeline/notes/${noteId}/void`);
      toast.success("Nota annullata");
      loadTimeline();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const canVoid = ["super_admin", "admin"].includes(user?.role);
  const ctCanWrite = ["super_admin", "admin", "amministratore", "property_manager"].includes(user?.role);

  if (!open) return null;
  const b = d?.booking || {};
  const apt = d?.apartment || {};
  const fin = d?.financial || {};
  const links = d?.links || {};
  const tpl = d?.templates || {};
  const comms = d?.communications || [];

  const phone = b.guest_whatsapp || b.guest_phone || "";
  const email = b.guest_email || "";

  const logComm = async (channel, template_type, recipient, body, subject = "") => {
    try {
      await api.post(`/bookings/${bookingId}/communications`, { booking_id: bookingId, channel, template_type, recipient, body, subject });
      load();
    } catch { /* non-blocking */ }
  };

  const composeCheckin = () =>
    tpl.checkin ||
    `Ciao ${b.guest_name || ""}, ecco le istruzioni per il check-in a ${apt.name || ""}${apt.address ? " (" + apt.address + ")" : ""}.` +
    `${apt.keybox_code ? " Codice keybox: " + apt.keybox_code + "." : ""}` +
    `${apt.wifi_name ? " WiFi: " + apt.wifi_name + " / " + (apt.wifi_password || "") + "." : ""}` +
    ` Check-in dal ${b.check_in || ""}. A presto!`;
  const composeWelcome = () =>
    (tpl.welcome ? tpl.welcome + " " : `Ciao ${b.guest_name || ""}, ecco la nostra Welcome Guide: `) + (links.welcome_guide_url || "");
  const composeDocuments = () =>
    (tpl.documents ? tpl.documents + " " : `Ciao ${b.guest_name || ""}, per completare la registrazione carica i tuoi documenti qui: `) + (links.tally_documents_url || "");
  const composeGeneric = () => `Ciao ${b.guest_name || ""}, ti scriviamo da Urban Dreams Home riguardo la tua prenotazione a ${apt.name || ""}.`;

  const sendWhatsApp = (template_type, body) => {
    if (!phone) { toast.error("Numero ospite mancante"); return; }
    window.open(`https://wa.me/${onlyDigits(phone)}?text=${encodeURIComponent(body)}`, "_blank");
    logComm("whatsapp", template_type, phone, body);
    toast.success("WhatsApp aperto · registrato in cronologia");
  };
  const sendEmail = (template_type, body, subject) => {
    if (!email) { toast.error("Email ospite mancante"); return; }
    window.open(`mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, "_blank");
    logComm("email", template_type, email, body, subject);
    toast.success("Email aperta · registrato in cronologia");
  };
  // template sends default to WhatsApp when phone present, else email
  const sendTemplate = (template_type, body, subject) => {
    if (phone) sendWhatsApp(template_type, body);
    else if (email) sendEmail(template_type, body, subject);
    else toast.error("Nessun contatto ospite (telefono/email)");
  };
  const needLink = (url, what) => { if (!url) { toast.error(`${what} non configurato · impostalo nella scheda appartamento o in Impostazioni`); return true; } return false; };

  const Row = ({ icon: Icon, label, value }) => (
    <div className="flex items-center gap-2 py-1.5 text-sm">
      <Icon size={14} className="shrink-0 text-slate-400" />
      <span className="w-32 shrink-0 text-slate-500">{label}</span>
      <span className="font-medium text-slate-900">{value || "—"}</span>
    </div>
  );

  const actions = [
    { key: "email", label: "Invia Email", icon: Mail, on: () => sendEmail("custom", composeGeneric(), `Urban Dreams Home · ${apt.name || ""}`) },
    { key: "whatsapp", label: "Apri WhatsApp", icon: MessageCircle, on: () => sendWhatsApp("custom", composeGeneric()) },
    { key: "auto-msg", label: "Messaggio automatico", icon: Send, on: () => sendTemplate("generic", composeGeneric(), "Urban Dreams Home") },
    { key: "tally", label: "Link Tally documenti", icon: FileText, on: () => { if (needLink(links.tally_documents_url, "Link Tally documenti")) return; sendTemplate("documents", composeDocuments(), "Documenti richiesti"); } },
    { key: "welcome", label: "Welcome Guide", icon: BookOpen, on: () => { if (needLink(links.welcome_guide_url, "Welcome Guide")) return; sendTemplate("welcome", composeWelcome(), "Welcome Guide"); } },
    { key: "checkin", label: "Istruzioni check-in", icon: KeyRound, on: () => sendTemplate("checkin", composeCheckin(), "Istruzioni check-in") },
    { key: "financial", label: "Financial Engine", icon: BarChart3, on: () => { onOpenChange(false); navigate(`/financial-engine?booking=${bookingId}`); } },
    { key: "block", label: "Blocca date", icon: CalendarOff, on: () => { onOpenChange(false); onBlockDates?.(b.apartment_id); } },
    { key: "maintenance", label: "Crea manutenzione", icon: Wrench, on: () => { onOpenChange(false); navigate(`/maintenance?apartment=${b.apartment_id}&new=1`); } },
    { key: "cleaning", label: "Crea pulizia", icon: Sparkles, on: () => { onOpenChange(false); navigate(`/cleanings?apartment=${b.apartment_id}&new=1`); } },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl" data-testid="booking-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User size={18} /> {b.guest_name || "Prenotazione"}
            {b.channel && <Badge className="ml-1 bg-slate-100 text-slate-600 capitalize">{b.channel}</Badge>}
          </DialogTitle>
          <DialogDescription>{apt.code ? apt.code + " · " : ""}{apt.name} · {d?.owner_name}</DialogDescription>
        </DialogHeader>

        {!d ? (
          <div className="py-10 text-center text-sm text-slate-400">Caricamento…</div>
        ) : (
          <div className="space-y-5">
            {/* top buttons */}
            <div className="flex flex-wrap gap-2">
              {canEdit && (
                <Button data-testid="bdd-edit-btn" onClick={() => { onOpenChange(false); onEdit?.(b); }} className="gap-2 bg-primary hover:bg-primary/90">
                  <Pencil size={15} /> Modifica prenotazione
                </Button>
              )}
            </div>

            <GuestPortalLink bookingId={bookingId} />

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              {/* Guest + booking */}
              <div className="rounded-xl border border-slate-200 p-4">
                <h4 className="mb-2 text-xs font-semibold uppercase text-slate-400">Ospite & soggiorno</h4>
                <Row icon={User} label="Nome" value={b.guest_name} />
                <Row icon={Phone} label="Telefono" value={b.guest_phone} />
                <Row icon={MessageCircle} label="WhatsApp" value={b.guest_whatsapp || b.guest_phone} />
                <Row icon={Mail} label="Email" value={b.guest_email} />
                <Row icon={Building2} label="Canale" value={b.channel} />
                <Row icon={FileCheck} label="Codice OTA" value={b.booking_reference} />
                <Row icon={Clock} label="Check-in" value={b.check_in} />
                <Row icon={Clock} label="Check-out" value={b.check_out} />
                <Row icon={User} label="Ospiti" value={b.guests} />
              </div>

              {/* Financial + status */}
              <div className="rounded-xl border border-slate-200 p-4">
                <h4 className="mb-2 text-xs font-semibold uppercase text-slate-400">Pagamenti & Financial Engine</h4>
                <Row icon={CreditCard} label="Importo totale" value={eur(fin.total)} />
                <Row icon={CreditCard} label="Incassato" value={eur(fin.paid)} />
                <div className="flex items-center gap-2 py-1.5 text-sm">
                  <CreditCard size={14} className="shrink-0 text-slate-400" />
                  <span className="w-32 shrink-0 text-slate-500">Saldo</span>
                  <span className={`font-semibold ${fin.balance > 0 ? "text-rose-600" : "text-emerald-700"}`}>{eur(fin.balance)}</span>
                </div>
                <Row icon={BarChart3} label="Ricavo lordo" value={eur(fin.gross)} />
                <Row icon={BarChart3} label="Commissione OTA" value={eur(fin.ota_commission)} />
                <Row icon={BarChart3} label="Ricavo netto" value={eur(fin.net)} />
                <div className="mt-2 flex flex-wrap gap-2">
                  <Badge className={b.payment_status === "pagato" ? "bg-emerald-100 text-emerald-700" : b.payment_status === "parziale" ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-700"}>Pag: {b.payment_status}</Badge>
                  <Badge className={b.document_status === "ricevuto" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}>Doc: {b.document_status}</Badge>
                </div>
                {b.notes && <p className="mt-3 border-t border-slate-100 pt-2 text-xs text-slate-500"><b>Note interne:</b> {b.notes}</p>}
              </div>
            </div>

            {/* Quick actions */}
            <div>
              <h4 className="mb-2 text-xs font-semibold uppercase text-slate-400">Azioni rapide & comunicazioni</h4>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
                {actions.map((a) => (
                  <Button key={a.key} data-testid={`bdd-action-${a.key}`} variant="outline" onClick={a.on}
                    className="h-auto flex-col gap-1.5 py-3 text-xs hover:border-primary hover:text-primary">
                    <a.icon size={17} /> {a.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Communications history */}
            <div>
              <h4 className="mb-2 text-xs font-semibold uppercase text-slate-400">Cronologia comunicazioni ({comms.length})</h4>
              <div className="max-h-56 space-y-2 overflow-y-auto" data-testid="bdd-comm-history">
                {comms.map((c) => (
                  <div key={c.id} className="rounded-lg border border-slate-200 p-2.5 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1.5 font-medium text-slate-800">
                        {c.channel === "whatsapp" ? <MessageCircle size={13} className="text-emerald-600" /> : <Mail size={13} className="text-blue-600" />}
                        {c.channel} · {c.template_type}
                      </span>
                      <span className="text-xs text-slate-400">{new Date(c.sent_at).toLocaleString("it-IT")}</span>
                    </div>
                    <div className="mt-1 text-xs text-slate-500">→ {c.recipient} · da {c.sent_by}</div>
                    {c.body && <div className="mt-1 line-clamp-2 text-xs text-slate-600">{c.body}</div>}
                  </div>
                ))}
                {comms.length === 0 && <p className="py-4 text-center text-sm text-slate-400">Nessuna comunicazione ancora inviata</p>}
              </div>
            </div>

            {/* Check-in Event Workflow (verifica documenti + condizioni accesso) */}
            <div className="border-t border-slate-100 pt-4">
              <CheckinWorkflowPanel bookingId={bookingId} onChange={loadTimeline} />
            </div>

            {/* Customer Journey (Communication Center v2 · Fase 2) */}
            <div className="border-t border-slate-100 pt-4">
              <CustomerJourney bookingId={bookingId} onChange={loadTimeline} />
            </div>

            {/* Timeline cronologica unificata */}
            <div className="border-t border-slate-100 pt-4">
              <CityTaxCollectPanel bookingId={bookingId} canWrite={ctCanWrite} onChange={loadTimeline} />
              <div className="mt-4">
                <Timeline
                events={tl?.events || []}
                loading={tlLoading}
                noteCategories={tl?.note_categories || []}
                notePriorities={tl?.note_priorities || []}
                canAddNote={true}
                canVoid={canVoid}
                onAddNote={addNote}
                onVoidNote={voidNote}
              />
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
